/**
 * Platform Admin API Service
 *
 * Separate API client for /api/platform-admin/* endpoints.
 * Uses distinct localStorage keys so platform-admin login
 * does not collide with tenant-admin login.
 */

const API_ORIGIN = import.meta.env.VITE_API_URL || '';
const PA_BASE_URL = `${API_ORIGIN}/api/platform-admin`;

const CSRF_COOKIE_NAME = 'quiz-commerce.x-csrf-token';
const STATE_CHANGING_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

// Separate storage keys — never collide with tenant-admin tokens
export const PA_ACCESS_TOKEN_KEY = 'pa_access_token';
export const PA_REFRESH_TOKEN_KEY = 'pa_refresh_token';
export const PA_USER_KEY = 'pa_user';

function getCookie(name: string): string | null {
  if (typeof document === 'undefined' || !document.cookie) return null;
  for (const cookie of document.cookie.split(';')) {
    const [cookieName, ...valueParts] = cookie.split('=');
    if (cookieName?.trim() === name) {
      const value = valueParts.join('=').trim();
      return value ? decodeURIComponent(value) : null;
    }
  }
  return null;
}

function getCsrfToken(): string | null {
  return getCookie(CSRF_COOKIE_NAME);
}

// ── Types ─────────────────────────────────────────────────────────────────

export interface PlatformAdminUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'SUPER_ADMIN';
  tenant_id: string;
}

interface PlatformLoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: PlatformAdminUser;
}

interface PlatformRefreshResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

// ── Core fetch helper ─────────────────────────────────────────────────────

async function platformFetch<T>(
  endpoint: string,
  options: RequestInit & { skipAuth?: boolean; unwrap?: boolean } = {}
): Promise<T> {
  const { skipAuth = false, unwrap = true, headers: customHeaders, ...fetchOptions } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...customHeaders as Record<string, string>,
  };

  if (!skipAuth) {
    const token = localStorage.getItem(PA_ACCESS_TOKEN_KEY);
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const method = (fetchOptions.method || 'GET').toUpperCase();
  if (STATE_CHANGING_METHODS.includes(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
  }

  const response = await fetch(`${PA_BASE_URL}${endpoint}`, {
    ...fetchOptions,
    headers,
    credentials: 'include',
  });

  if (response.status === 401 && !skipAuth) {
    localStorage.removeItem(PA_ACCESS_TOKEN_KEY);
    localStorage.removeItem(PA_REFRESH_TOKEN_KEY);
    localStorage.removeItem(PA_USER_KEY);
    window.dispatchEvent(new CustomEvent('platform-auth:expired'));
    throw new Error('Sitzung abgelaufen — bitte erneut einloggen.');
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({})) as Record<string, unknown>;
    throw new Error(
      (errorBody.message as string) ||
      (errorBody.error as string) ||
      `Request fehlgeschlagen (${response.status})`
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const body = (await response.json()) as unknown;
  // unwrap:false → caller wants the raw body because the endpoint does not use
  // the { success, data } envelope. See startImpersonation/stopImpersonation.
  if (!unwrap) {
    return body as T;
  }
  return (body as ApiResponse<T>).data;
}

// ── Public API ────────────────────────────────────────────────────────────

export async function platformAdminLogin(
  email: string,
  password: string
): Promise<PlatformAdminUser> {
  const response = await platformFetch<PlatformLoginResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
    skipAuth: true,
  });

  localStorage.setItem(PA_ACCESS_TOKEN_KEY, response.access_token);
  localStorage.setItem(PA_REFRESH_TOKEN_KEY, response.refresh_token);
  localStorage.setItem(PA_USER_KEY, JSON.stringify(response.user));

  return response.user;
}

export async function platformAdminRefresh(): Promise<void> {
  const refreshToken = localStorage.getItem(PA_REFRESH_TOKEN_KEY);
  if (!refreshToken) throw new Error('No refresh token');

  const response = await platformFetch<PlatformRefreshResponse>('/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refresh_token: refreshToken }),
    skipAuth: true,
  });

  localStorage.setItem(PA_ACCESS_TOKEN_KEY, response.access_token);
  localStorage.setItem(PA_REFRESH_TOKEN_KEY, response.refresh_token);
}

export function platformAdminLogout(): void {
  localStorage.removeItem(PA_ACCESS_TOKEN_KEY);
  localStorage.removeItem(PA_REFRESH_TOKEN_KEY);
  localStorage.removeItem(PA_USER_KEY);
}

export function getPlatformAdminUser(): PlatformAdminUser | null {
  const raw = localStorage.getItem(PA_USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PlatformAdminUser;
  } catch {
    return null;
  }
}

export function isPlatformAdminAuthenticated(): boolean {
  return !!localStorage.getItem(PA_ACCESS_TOKEN_KEY);
}

// ── Dashboard ────────────────────────────────────────────────────────────

export interface DashboardMetrics {
  tenants: {
    total: number;
    active: number;
    suspended: number;
    trial: number;
    byTier: Record<string, number>;
  };
  quizzes: {
    total: number;
    published: number;
    draft: number;
  };
  products: {
    total: number;
  };
  completions: {
    total: number;
    last7Days: number;
    last30Days: number;
  };
  recentTenants: Array<{
    id: string;
    name: string;
    slug: string;
    subscriptionTier: string;
    status: string;
    createdAt: string;
  }>;
}

export async function fetchDashboardMetrics(): Promise<DashboardMetrics> {
  return platformFetch<DashboardMetrics>('/dashboard');
}

// ── Tenants ─────────────────────────────────────────────────────────────

export interface TenantListItem {
  id: string;
  name: string;
  slug: string;
  subscriptionTier: string;
  status: string;
  userCount: number;
  quizCount: number;
  productCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface TenantListResponse {
  tenants: TenantListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface TenantListParams {
  page?: number;
  pageSize?: number;
  status?: string;
  tier?: string;
  search?: string;
  sortBy?: 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

export async function fetchTenants(params: TenantListParams = {}): Promise<TenantListResponse> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params.status) searchParams.set('status', params.status);
  if (params.tier) searchParams.set('tier', params.tier);
  if (params.search) searchParams.set('search', params.search);
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);
  const query = searchParams.toString();
  return platformFetch<TenantListResponse>(`/tenants${query ? `?${query}` : ''}`);
}

export async function deleteTenant(id: string): Promise<void> {
  await platformFetch<void>(`/tenants/${id}`, { method: 'DELETE' });
}

export interface TenantDetailUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
}

export interface TenantDetail {
  id: string;
  name: string;
  slug: string;
  subscriptionTier: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  stats: {
    userCount: number;
    quizCount: number;
    productCount: number;
    activeQuizCount: number;
  };
  users: TenantDetailUser[];
}

export async function fetchTenantById(id: string): Promise<TenantDetail> {
  const body = await platformFetch<{ tenant: TenantDetail }>(`/tenants/${id}`);
  return body.tenant;
}

// ── Audit Logs ──────────────────────────────────────────────────────────

export interface AuditLogEntry {
  id: string;
  adminUserId: string;
  action: string;
  resourceType: string;
  resourceId: string | null;
  tenantId: string | null;
  details: Record<string, unknown>;
  ipAddress: string;
  createdAt: string;
}

export interface AuditLogPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface AuditLogResponse {
  logs: AuditLogEntry[];
  pagination: AuditLogPagination;
}

export interface AuditLogParams {
  page?: number;
  pageSize?: number;
  action?: string;
  tenantId?: string;
  adminUserId?: string;
  from?: string;
  to?: string;
}

export async function fetchAuditLogs(params: AuditLogParams = {}): Promise<AuditLogResponse> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params.action) searchParams.set('action', params.action);
  if (params.tenantId) searchParams.set('tenantId', params.tenantId);
  if (params.adminUserId) searchParams.set('adminUserId', params.adminUserId);
  if (params.from) searchParams.set('from', params.from);
  if (params.to) searchParams.set('to', params.to);
  const query = searchParams.toString();
  return platformFetch<AuditLogResponse>(`/audit-logs${query ? `?${query}` : ''}`);
}

// ── Impersonation ──────────────────────────────────────────────────────────

// Trigger-Klassifikation — Werte exakt aus startImpersonationSchema (Backend).
export type ImpersonationReason =
  | 'onboarding-support'
  | 'bug-investigation'
  | 'data-check'
  | 'configuration-help'
  | 'other';

export interface ImpersonationSessionInfo {
  id: string;
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  reason: string;
  startedAt: string;
}

export interface StartImpersonationResponse {
  impersonationToken: string;
  expiresAt: string;
  session: ImpersonationSessionInfo;
}

export async function startImpersonation(
  tenantId: string,
  reason: ImpersonationReason
): Promise<StartImpersonationResponse> {
  // Konventionskonform enveloped ({ success, data }) durch die
  // responseWrapperMiddleware — platformFetch packt body.data aus und liefert
  // genau StartImpersonationResponse.
  return platformFetch<StartImpersonationResponse>(`/impersonate/${tenantId}`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function stopImpersonation(sessionId: string): Promise<void> {
  // Konventionskonform enveloped — siehe startImpersonation; der ausgepackte
  // { message } wird nicht gelesen, der await reicht zur Konsistenz.
  await platformFetch<{ message: string }>('/impersonate', {
    method: 'DELETE',
    body: JSON.stringify({ sessionId }),
  });
}
